/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getChannelsEndpoint } from '@gitbook/env';
import { Logger } from '@gitbook/util-debug';
import { useLiveRef, useOnPageBecomeActive } from '@gitbook/util-react-hooks';

/**
 * Timeout for closing a channel that has no listeners.
 */
const CHANNEL_GC_TIMEOUT = 10000;

/**
 * Delay before the first reconnection attempt. It doubles on every failed attempt,
 * up to `MAX_RECONNECT_DELAY`, and resets once the stream opens again.
 */
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

interface OpenedChannel {
    eventSource: EventSource;
    data: undefined | api.APIChannelData['data'];
    listeners: Array<(data: api.APIChannelData['data']) => void>;
    gcTimeout?: NodeJS.Timeout;
    /** Backoff delay for the next reconnection attempt. */
    reconnectDelay: number;
    reconnectTimeout?: NodeJS.Timeout;
    /** Whether the stream dropped since it last opened, making the next open a *re*connection. */
    hasDisconnected: boolean;
}

const channels = new Map<string, OpenedChannel>();

/**
 * Listeners notified when channels reopen after a disconnection. Consumers of the
 * channels can miss messages while the stream is down, so this is their cue to resync.
 */
const reconnectedListeners = new Set<() => void>();
let notifyReconnectedTimeout: NodeJS.Timeout | undefined;

const logger = Logger('channels');

/**
 * React hook to listen to updates on a realtime channel.
 */
export function useRealtimeChannel<Channel extends api.APIChannel>(
    channel: Channel,
    /** Optional callback for each update received */
    callback?: (data: api.DataForSubscriptionChannel<Channel>) => void,
    options?: { skip?: boolean }
): api.DataForSubscriptionChannel<Channel> | null {
    const channelId = api.getSubscriptionChannelId(channel);
    const skip = options?.skip ?? false;

    const [data, setData] = React.useState<api.DataForSubscriptionChannel<Channel> | null>(() => {
        const channel = channels.get(channelId);
        return (channel?.data as api.DataForSubscriptionChannel<Channel>) ?? null;
    });
    const callbackRef = useLiveRef(callback);

    React.useEffect(() => {
        if (skip) {
            setData(null);
            return;
        }
        return subscribeToRealtimeChannelId<api.DataForSubscriptionChannel<Channel>>(
            channelId,
            (newData) => {
                setData(newData);
                if (callbackRef.current) {
                    callbackRef.current(newData);
                }
            }
        );
    }, [channelId, callbackRef, skip]);

    return data;
}

/**
 * Get the initial data for a channel if any.
 */
export function getInitialDataForChannel<Channel extends api.APIChannel>(
    channel: Channel
): api.DataForSubscriptionChannel<Channel> | null {
    const channelId = api.getSubscriptionChannelId(channel);
    const existingChannel = channels.get(channelId);
    return (existingChannel?.data as api.DataForSubscriptionChannel<Channel>) ?? null;
}

/**
 * Subscribe to updates on a realtime channel.
 */
export function subscribeToRealtimeChannel<Channel extends api.APIChannel>(
    channel: Channel,
    callback: (data: api.DataForSubscriptionChannel<Channel>) => void
): () => void {
    const channelId = api.getSubscriptionChannelId(channel);
    const data = getInitialDataForChannel(channel);
    if (data) {
        callback(data);
    }
    return subscribeToRealtimeChannelId(channelId, callback);
}

/**
 * Subscribe to a realtime channel by ID.
 */
export function subscribeToRealtimeChannelId<Data extends api.APIChannelData['data']>(
    channelId: string,
    callback: (data: Data) => void
): () => void {
    let channel = channels.get(channelId);
    if (!channel) {
        logger.info(`opening channel "${channelId}"`);
        channel = {
            eventSource: createChannelEventSource(channelId),
            data: undefined,
            listeners: [],
            reconnectDelay: INITIAL_RECONNECT_DELAY,
            hasDisconnected: false,
        };
        channels.set(channelId, channel);
    }

    channel.listeners.push(callback);
    return () => {
        channel.listeners = channel.listeners.filter((fn) => fn !== callback);

        if (channel.listeners.length === 0) {
            if (channel.gcTimeout) {
                clearTimeout(channel.gcTimeout);
            }

            // We don't directly close the channel here, as it might need to be used directly after because of react effect transition
            // It also helps ensure durign development where react calls hooks multiple times, we don't churn the websocket too often.
            channel.gcTimeout = setTimeout(() => {
                garbageCollectChannel(channelId);
            }, CHANNEL_GC_TIMEOUT);
        }
    };
}

/**
 * Reopen every channel whose stream is closed, bypassing any pending backoff delay.
 *
 * Safe to call at any time: channels that are open or already connecting are left alone.
 * The server replays the channel's last message on connect, so the data of a reopened
 * channel catches up on its own.
 */
export function reconnectAllChannels(): void {
    channels.forEach((channel, channelId) => {
        // A stream still open or negotiating does not need our help. Unlike a WebSocket
        // there is no way to probe an EventSource, so a half-open stream reporting `OPEN`
        // is left to the query-level refetch on focus.
        if (
            channel.eventSource.readyState !== EventSource.CLOSED &&
            channel.reconnectTimeout === undefined
        ) {
            return;
        }

        logger.info(`reconnecting channel "${channelId}"`);
        clearScheduledReconnection(channel);
        channel.reconnectDelay = INITIAL_RECONNECT_DELAY;
        channel.eventSource.close();
        channel.eventSource = createChannelEventSource(channelId);
    });
}

/**
 * Listen for channels reopening after a disconnection, to resync anything that could
 * have missed a message while the stream was down. Returns a function to unsubscribe.
 */
export function onChannelsReconnected(listener: () => void): () => void {
    reconnectedListeners.add(listener);
    return () => {
        reconnectedListeners.delete(listener);
    };
}

/**
 * Keep the realtime channels alive across network drops and page sleep.
 *
 * Should be mounted once, high in the application tree.
 */
export function useRealtimeChannelsReliability(): void {
    useOnPageBecomeActive(reconnectAllChannels);

    React.useEffect(() => {
        window.addEventListener('online', reconnectAllChannels);
        return () => {
            window.removeEventListener('online', reconnectAllChannels);
        };
    }, []);
}

/**
 * Open the event source of a channel, and keep it alive.
 */
function createChannelEventSource(channelId: string): EventSource {
    const eventSource = new EventSource(getEventSourceURL(channelId));

    eventSource.addEventListener('message', (event) => {
        const channel = channels.get(channelId);
        const parsed = parseEventSourceMessage<api.APIChannelData['data']>(event.data);
        logger.info(`received message from channel "${channelId}"`, parsed);
        if (parsed !== null && channel) {
            channel.data = parsed;
            channel.listeners.forEach((fn) => {
                fn(parsed);
            });
        }
    });

    eventSource.addEventListener('open', () => {
        const channel = channels.get(channelId);
        if (!channel) {
            return;
        }

        channel.reconnectDelay = INITIAL_RECONNECT_DELAY;
        clearScheduledReconnection(channel);

        if (channel.hasDisconnected) {
            logger.info(`channel "${channelId}" reconnected`);
            channel.hasDisconnected = false;
            notifyChannelsReconnected();
        }
    });

    eventSource.addEventListener('error', () => {
        const channel = channels.get(channelId);
        if (!channel) {
            return;
        }

        channel.hasDisconnected = true;

        // `CONNECTING` means the browser is retrying the stream on its own. It gives up
        // for good on an HTTP error, leaving the stream `CLOSED` with nothing else coming.
        if (eventSource.readyState === EventSource.CLOSED) {
            logger.error(`channel "${channelId}" closed on error`);
            scheduleChannelReconnection(channelId);
        }
    });

    return eventSource;
}

/**
 * Reopen a closed channel after its backoff delay.
 */
function scheduleChannelReconnection(channelId: string) {
    const channel = channels.get(channelId);
    if (!channel || channel.reconnectTimeout) {
        return;
    }

    const delay = channel.reconnectDelay;
    logger.info(`scheduling reconnection of channel "${channelId}" in ${delay}ms`);

    channel.reconnectTimeout = setTimeout(() => {
        // The channel could have been garbage collected while waiting.
        const current = channels.get(channelId);
        if (!current) {
            return;
        }

        current.reconnectTimeout = undefined;
        current.reconnectDelay = Math.min(delay * 2, MAX_RECONNECT_DELAY);
        current.eventSource.close();
        current.eventSource = createChannelEventSource(channelId);
    }, delay);
}

function clearScheduledReconnection(channel: OpenedChannel) {
    if (channel.reconnectTimeout) {
        clearTimeout(channel.reconnectTimeout);
        channel.reconnectTimeout = undefined;
    }
}

/**
 * Notify the reconnection listeners, coalescing a burst of channels reopening at once
 * (a network coming back takes them all down together) into a single notification.
 */
function notifyChannelsReconnected() {
    if (notifyReconnectedTimeout) {
        return;
    }

    notifyReconnectedTimeout = setTimeout(() => {
        notifyReconnectedTimeout = undefined;
        reconnectedListeners.forEach((listener) => {
            listener();
        });
    }, 0);
}

function garbageCollectChannel(channelId: string) {
    const channel = channels.get(channelId);
    if (!channel) {
        return;
    }

    channel.gcTimeout = undefined;

    if (channel.listeners.length > 0) {
        return;
    }

    logger.info(`close channel "${channelId}"`);
    clearScheduledReconnection(channel);
    channel.eventSource.close();
    channels.delete(channelId);
}

/**
 * Get the WebSocket URL for a channel ID.
 */
function getEventSourceURL(channelId: string): URL {
    return new URL(`${getChannelsEndpoint()}/channels/${encodeURIComponent(channelId)}/events`);
}

/**
 * Parse an event source message.
 */
function parseEventSourceMessage<Data>(data: unknown): Data | null {
    if (typeof data !== 'string') {
        return null;
    }

    try {
        const parsed = JSON.parse(data) as unknown;
        if (parsed && typeof parsed === 'object' && 'data' in parsed) {
            // @ts-expect-error
            return parsed.data;
        }

        throw new Error('invalid event source message, missing "data" field');
    } catch (_err) {
        logger.error('failed to parse event source message', data, _err);
        return null;
    }
}
